import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/kvkk-consent`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");
const errMsg = (e) => e.response?.data?.detail || "İşlem tamamlanamadı";

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
};

const TextBlock = ({ title, text, testid }) => (
  <section className="space-y-1">
    <h2 className="font-bold text-base">{title}</h2>
    <div className="rounded-xl border bg-muted/30 p-3 text-sm whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid={testid}>{text}</div>
  </section>
);

const SharedData = ({ c }) => (
  <section className="rounded-xl border p-3 text-sm space-y-2" data-testid="kvkk-shared-data">
    <div><b>Paylaşılacak bilgiler:</b><ul className="list-disc pl-5 mt-1">{c.shared_data.map((s) => <li key={s}>{s}</li>)}</ul></div>
    <div><b>Kimlerle:</b> {c.recipients}</div>
    <div><b>Amaç:</b> {c.purpose}</div>
    <div className="text-xs text-muted-foreground">Talep: {c.route} · {c.transport_category} · {c.customer_display} · {c.phone_masked}</div>
  </section>
);

const GrantForm = ({ onGrant, busy }) => {
  const [ack, setAck] = useState(false);
  const [riza, setRiza] = useState(false);
  return (
    <div className="space-y-3">
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} data-testid="kvkk-ack" />Aydınlatma metnini okudum ve anladım.</label>
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={riza} onCheckedChange={(v) => setRiza(v === true)} data-testid="kvkk-riza" />Açık rıza metninde belirtilen bilgilerimin, belirtilen hizmet sağlayıcılarla belirtilen amaçla paylaşılmasına açık rıza veriyorum.</label>
      <Button disabled={!ack || !riza || busy} onClick={() => onGrant(ack, riza)} data-testid="kvkk-grant" className="w-full rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Onaylıyorum
      </Button>
      <p className="text-xs text-muted-foreground">Onay vermek zorunlu değildir. Onay vermezseniz bilgileriniz hizmet sağlayıcılarla paylaşılmaz.</p>
    </div>
  );
};

export default function KvkkConsent() {
  useNoIndex();
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/${token}`).then(({ data }) => setC(data)).catch((e) => setError(errMsg(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, msg) => {
    setBusy(true);
    try { await axios.post(`${API}/${token}/${path}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const grant = (ack, riza) => act("grant", { aydinlatma_acknowledged: ack, explicit_consent: riza }, "Onayınız kaydedildi");
  const revoke = () => { if (window.confirm("Açık rızanızı geri almak istiyor musunuz? İlanınız yayından kaldırılır.")) act("revoke", {}, "Rızanız geri alındı"); };

  if (error) return <div className="mx-auto max-w-2xl px-4 py-10 text-center text-sm text-red-600" data-testid="kvkk-error">{error}</div>;
  if (!c) return <div className="mx-auto max-w-2xl px-4 py-10 text-center text-sm text-muted-foreground">Yükleniyor…</div>;
  return (
    <div className="mx-auto max-w-2xl px-4 py-6 space-y-4" data-testid="kvkk-page">
      <h1 className="text-2xl font-extrabold flex items-center gap-2"><ShieldCheck className="w-6 h-6" />Kişisel Veri Paylaşım Onayı</h1>
      <SharedData c={c} />
      <TextBlock title="Aydınlatma Metni" text={c.aydinlatma_text} testid="kvkk-aydinlatma" />
      <TextBlock title="Açık Rıza Metni" text={c.acik_riza_text} testid="kvkk-riza-text" />
      <div className="text-xs text-muted-foreground">Metin sürümü: v{c.text_version}</div>
      {c.status === "pending" && !c.expired && <GrantForm onGrant={grant} busy={busy} />}
      {c.status === "pending" && c.expired && <div className="text-sm text-red-600" data-testid="kvkk-expired">Bu bağlantının süresi doldu. Lütfen Yol Hattı ile iletişime geçin.</div>}
      {c.status === "granted" && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 space-y-2" data-testid="kvkk-granted">
          <div className="flex items-center gap-2 text-sm font-semibold"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Onayınız {fmt(c.granted_at)} tarihinde kaydedildi.</div>
          <Button variant="outline" size="sm" onClick={revoke} disabled={busy} data-testid="kvkk-revoke">Rızamı geri al</Button>
        </div>
      )}
      {c.status === "revoked" && <div className="flex items-center gap-2 text-sm" data-testid="kvkk-revoked"><XCircle className="w-4 h-4" />Rızanız {fmt(c.revoked_at)} tarihinde geri alındı. Bilgileriniz artık yeni hizmet sağlayıcılarla paylaşılmıyor.</div>}
      {c.status === "superseded" && <div className="text-sm text-muted-foreground" data-testid="kvkk-superseded">Bu bağlantı artık geçerli değil.</div>}
    </div>
  );
}
